import { useReducer, useEffect, useCallback } from 'react'
import api, { IDepartment } from '@api'
import { debounce } from 'lodash'
import { ApolloError } from 'apollo-boost'
import { Toaster } from '@ui'

enum Actions {
  SEARCH,
  LOAD,
}
interface IState {
  loading: boolean
  query: string
  items: IDepartment[]
  reqId: number
}

interface SearchAction {
  type: Actions.SEARCH
  query: string
  reqId: number
}
interface LoadAction {
  type: Actions.LOAD
  items: IDepartment[]
  reqId: number
}
type Action = LoadAction | SearchAction

function reducer(state: IState, action: Action) {
  switch (action.type) {
    case Actions.SEARCH:
      return {
        ...state,
        reqId: action.reqId,
        query: action.query,
        loading: true,
      }
    case Actions.LOAD:
      return state.reqId > action.reqId
        ? state
        : {
            ...state,
            items: action.items,
            loading: false,
          }
    default:
      return state
  }
}

export default function useDepartmentSearch(): IState & {
  run: (query: string) => void
} {
  const [state, dispatch] = useReducer(reducer, {
    reqId: 0,
    loading: false,
    query: '',
    items: [],
  })

  const debouncedSearch = useCallback(
    debounce((queryString: string, reqId: number) => {
      api.department
        .search({ query: queryString })
        .then((departmentConnection) => {
          dispatch({
            type: Actions.LOAD,
            reqId,
            items: departmentConnection.edges.map(({ node }) => node),
          })
        })
        .catch((error: ApolloError) => {
          Toaster.fail({ error })
        })
    }, 300),
    []
  )

  useEffect(() => {
    const reqId = Date.now()

    if (!state.query) {
      return dispatch({
        type: Actions.LOAD,
        reqId,
        items: [],
      })
    }

    debouncedSearch(state.query, reqId)

    return () => debouncedSearch.cancel()
  }, [state.query, debouncedSearch])

  return {
    ...state,
    run: (query: string) =>
      dispatch({
        type: Actions.SEARCH,
        query,
        reqId: Date.now(),
      }),
  }
}
